package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import info.rezident.app.ui.theme.AppTheme
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*

// Slide up animation for bottom sheet
val slideUpKeyframes = Keyframes {
    0.percent {
        Modifier.translateY(100.percent)
    }
    100.percent {
        Modifier.translateY(0.percent)
    }
}

// Slide down animation for bottom sheet
val slideDownKeyframes = Keyframes {
    0.percent {
        Modifier.translateY(0.percent)
    }
    100.percent {
        Modifier.translateY(100.percent)
    }
}

// Fade in animation for backdrop
val fadeInKeyframes = Keyframes {
    0.percent {
        Modifier.opacity(0)
    }
    100.percent {
        Modifier.opacity(1)
    }
}

// Fade out animation for backdrop
val fadeOutKeyframes = Keyframes {
    0.percent {
        Modifier.opacity(1)
    }
    100.percent {
        Modifier.opacity(0)
    }
}

/**
 * Bottom sheet modal component that slides up from the bottom
 *
 * @param isVisible Whether the bottom sheet is visible
 * @param onDismiss Callback when the bottom sheet should be dismissed
 * @param content The content to display in the bottom sheet
 */
@Composable
fun BottomSheet(
    isVisible: Boolean,
    onDismiss: () -> Unit,
    content: @Composable () -> Unit
) {
    var shouldRender by remember { mutableStateOf(false) }
    var touchStartY by remember { mutableStateOf(0.0) }
    var currentTranslateY by remember { mutableStateOf(0.0) }
    var isDragging by remember { mutableStateOf(false) }
    var canDrag by remember { mutableStateOf(true) }

    LaunchedEffect(isVisible) {
        if (isVisible) {
            shouldRender = true
            currentTranslateY = 0.0
            isDragging = false
            canDrag = true
        } else if (shouldRender) {
            delay(300) // Wait for animation to complete
            shouldRender = false
            currentTranslateY = 0.0
            isDragging = false
            canDrag = true
        }
    }

    if (shouldRender) {
        // Backdrop overlay
        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .top(0.px)
                .left(0.px)
                .width(100.percent)
                .height(100.percent)
                .backgroundColor(rgba(0, 0, 0, 0.5))
                .zIndex(999)
                .cursor(Cursor.Pointer)
                .onClick { onDismiss() }
                .animation(
                    if (isVisible) {
                        fadeInKeyframes.toAnimation(
                            duration = 300.ms,
                            fillMode = AnimationFillMode.Forwards
                        )
                    } else {
                        fadeOutKeyframes.toAnimation(
                            duration = 300.ms,
                            fillMode = AnimationFillMode.Forwards
                        )
                    }
                )
        )

        // Bottom sheet container
        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .bottom(0.px)
                .left(0.px)
                .width(100.percent)
                .maxHeight(90.percent)
                .backgroundColor(AppTheme.colors.background)
                .borderRadius(topLeft = 16.px, topRight = 16.px)
                .zIndex(1000)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("box-shadow", "0 -4px 20px rgba(0, 0, 0, 0.15)")
                    if (isDragging && canDrag) {
                        property("transform", "translateY(${currentTranslateY}px)")
                        property("transition", "none")
                    } else if (currentTranslateY > 0) {
                        property("transform", "translateY(0px)")
                        property("transition", "transform 0.3s ease-out")
                    }
                }
                .then(
                    if (!isDragging) {
                        Modifier.animation(
                            if (isVisible) {
                                slideUpKeyframes.toAnimation(
                                    duration = 300.ms,
                                    timingFunction = AnimationTimingFunction.EaseOut,
                                    fillMode = AnimationFillMode.Forwards
                                )
                            } else {
                                slideDownKeyframes.toAnimation(
                                    duration = 300.ms,
                                    timingFunction = AnimationTimingFunction.EaseIn,
                                    fillMode = AnimationFillMode.Forwards
                                )
                            }
                        )
                    } else {
                        Modifier
                    }
                )
                .onTouchStart { event ->
                    // Check if touch started on an interactive element
                    fun isInteractiveElement(target: dynamic): Boolean {
                        var element = target
                        while (element != null) {
                            val tag = element.tagName?.toString()?.lowercase()
                            val role = element.getAttribute("role")?.toString()

                            // Check for interactive elements
                            if (tag == "button" || tag == "a" || tag == "input" ||
                                tag == "textarea" || tag == "select" ||
                                role == "button" || role == "link" ||
                                element.onclick != null ||
                                element.hasAttribute("data-interactive") == true
                            ) {
                                return true
                            }
                            element = element.parentElement
                        }
                        return false
                    }

                    canDrag = !isInteractiveElement(event.target)

                    if (canDrag) {
                        val touch = event.touches.item(0)
                        if (touch != null) {
                            touchStartY = touch.clientY.toDouble()
                            currentTranslateY = 0.0
                        }
                    }
                }
                .onTouchMove { event ->
                    if (canDrag) {
                        val touch = event.touches.item(0)
                        if (touch != null) {
                            val deltaY = touch.clientY.toDouble() - touchStartY
                            // Only allow dragging down (positive deltaY) with 10px threshold
                            if (deltaY > 10) {
                                isDragging = true
                                currentTranslateY = deltaY
                                event.preventDefault()
                            }
                        }
                    }
                }
                .onTouchEnd {
                    if (canDrag && isDragging) {
                        isDragging = false
                        // Dismiss if dragged more than 100px down
                        if (currentTranslateY > 100) {
                            onDismiss()
                        } else {
                            // Reset position with animation
                            currentTranslateY = 0.0
                        }
                    }
                    canDrag = true
                }
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .overflow(Overflow.Auto)
                    .padding(topBottom = 8.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Handle bar - visual indicator for swipe gesture
                Box(
                    modifier = Modifier
                        .width(40.px)
                        .height(4.px)
                        .backgroundColor(AppTheme.colors.textTertiary)
                        .borderRadius(2.px)
                        .margin(bottom = 16.px)
                )

                // Content
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 24.px, bottom = 24.px)
                ) {
                    content()
                }
            }
        }
    }
}
